import type { Messages } from '../i18n/messages';

/**
 * Help module chrome: nav labels, page titles, section headings, and short
 * link/button chrome. The long instructional body prose that teaches how the
 * portal works stays en-only (unkeyed) — see CLAUDE.md "In-app Help docs".
 * Brand/product names (GitHub, Jira, Sentry, SonarQube, Kafka, Dependabot,
 * "Merge Lord") pass through `t()` for architecture consistency but keep an
 * identical `fun` value — a fun rewrite of a proper noun would be inaccurate.
 */
export const helpMessages: Messages = {
  // --- HelpNav ---
  'help.nav.overview': { en: 'Overview', fun: 'Lay of the Land' },
  'help.nav.dailyDashboard': { en: 'Daily dashboard', fun: 'Your Daily Grind' },
  'help.nav.approvals': { en: 'Approvals', fun: 'Thumbs Up or Down' },
  'help.nav.ariaLabel': { en: 'Help navigation', fun: 'Help HQ' },
  'help.nav.collapseAction': {
    en: 'Collapse {label}',
    fun: 'Tuck away {label}',
  },
  'help.nav.expandAction': { en: 'Expand {label}', fun: 'Pop open {label}' },

  // --- Page titles (shared between HelpNav labels and *DocPage titles where
  // the text is identical) ---
  'help.title.help': { en: 'Help', fun: 'Manual' },
  'help.title.home': {
    en: 'Home — your daily dashboard',
    fun: 'Home — Mission Control',
  },
  'help.title.catalog': { en: 'Catalog', fun: 'Big Registry' },
  'help.title.approvals': { en: 'Approval rules', fun: 'Rulebook' },
  'help.title.integrations': { en: 'Integrations', fun: 'Gadget Drawer' },
  'help.title.adminDashboard': {
    en: 'Admin dashboard',
    fun: 'Mission Control (Admin Edition)',
  },
  'help.title.workflows': { en: 'Workflows', fun: 'Button Factory' },
  'help.title.others': { en: 'Others', fun: 'Grab Bag' },
  'help.title.deployments': { en: 'Deployments', fun: 'Launch Pad' },
  'help.title.kafkaQuery': { en: 'Kafka Query', fun: 'Kafka Interrogation' },

  // --- KafkaQueryDocView ---
  'help.kafkaQuery.whatTitle': { en: 'What it does', fun: 'What it does' },
  'help.kafkaQuery.queryTitle': {
    en: 'Building a query',
    fun: 'Building a query',
  },
  'help.kafkaQuery.resultsTitle': {
    en: 'Reading the result',
    fun: 'Reading the result',
  },
  'help.kafkaQuery.noTimestampTitle': {
    en: 'Kafka records no consume time',
    fun: 'Kafka records no consume time',
  },
  'help.kafkaQuery.limitsTitle': {
    en: 'Scan limits and honest reporting',
    fun: 'Scan limits and honest reporting',
  },
  'help.kafkaQuery.flowTitle': { en: 'How a query runs', fun: 'How a query runs' },
  'help.kafkaQuery.linksTitle': { en: 'Links to Kafka UI', fun: 'Links to Kafka UI' },

  // --- DocsHubView ---
  'help.hub.connectsTitle': {
    en: 'How it all connects',
    fun: 'How the Sausage Gets Made',
  },
  'help.hub.topicsTitle': { en: 'Topics', fun: 'Pick Your Adventure' },

  // --- HomeDocView ---
  'help.home.overviewTitle': {
    en: 'Your daily dashboard',
    fun: 'Your Daily Dashboard, Explained',
  },
  'help.home.panelsTitle': {
    en: 'What each panel shows',
    fun: "What's Behind Each Panel",
  },
  'help.home.refreshTitle': {
    en: 'Refreshing your data',
    fun: 'Hitting the Refresh Button',
  },
  'help.home.mergeLordTitle': { en: 'Merge Lord', fun: 'Merge Lord' },
  'help.home.badgesTitle': {
    en: 'Sidebar badges',
    fun: 'Those Little Numbers in the Sidebar',
  },
  'help.home.feedbackTitle': {
    en: 'Sending feedback',
    fun: 'Shout Into the Void (Productively)',
  },
  'help.home.routineTitle': {
    en: 'A suggested daily routine',
    fun: 'Your Suggested Daily Ritual',
  },
  'help.home.quicklinksTitle': { en: 'Quicklinks', fun: 'Quicklinks' },

  // --- OthersDocView ---
  'help.others.overviewTitle': {
    en: 'Cross-cutting chrome',
    fun: 'Odds & Ends',
  },

  // --- CatalogDocView ---
  'help.catalog.whatTitle': {
    en: 'What the catalog is',
    fun: 'What Even Is the Catalog',
  },
  'help.catalog.discoveryTitle': {
    en: 'How services get here',
    fun: 'How Services Show Up Here',
  },
  'help.catalog.tabsTitle': {
    en: 'Entity tabs & cards',
    fun: 'Tabs & Cards on a Service Page',
  },
  'help.catalog.ownershipTitle': { en: 'Ownership', fun: 'Whose Baby Is This' },
  'help.catalog.changingConfigTitle': {
    en: 'Changing config',
    fun: 'Tweaking the Knobs',
  },

  // --- ApprovalsDocView ---
  'help.approvals.requestTitle': {
    en: 'What an approval is',
    fun: 'What an Approval Actually Is',
  },
  'help.approvals.gitopsKindsTitle': {
    en: 'Config approvals — gitops kinds',
    fun: 'Config Approvals — the GitOps Squad',
  },
  'help.approvals.releaseKindsTitle': {
    en: 'Release approvals',
    fun: 'Release Approvals',
  },
  'help.approvals.deploymentKindsTitle': {
    en: 'Deployment approvals',
    fun: 'Deployment Approvals',
  },
  'help.approvals.migrationKindsTitle': {
    en: 'Migration approvals',
    fun: 'Migration Approvals',
  },
  'help.approvals.repositoryKindsTitle': {
    en: 'Repository approvals',
    fun: 'Repository Approvals',
  },
  'help.approvals.approversTitle': {
    en: 'Who gets asked',
    fun: 'Who Gets the Ping',
  },
  'help.approvals.actingTitle': {
    en: 'Approving or rejecting',
    fun: 'Yay or Nay',
  },
  'help.approvals.afterApproveTitle': {
    en: 'What happens after you approve',
    fun: 'What Happens After You Hit Approve',
  },
  'help.approvals.retentionTitle': {
    en: 'How long approvals stick around',
    fun: 'How Long Approvals Stick Around',
  },

  // --- IntegrationsDocView (brand names: identical en/fun) ---
  'help.integrations.githubTitle': { en: 'GitHub', fun: 'GitHub' },
  'help.integrations.jiraTitle': { en: 'Jira', fun: 'Jira' },
  'help.integrations.dependabotTitle': { en: 'Dependabot', fun: 'Dependabot' },
  'help.integrations.sentryTitle': { en: 'Sentry', fun: 'Sentry' },
  'help.integrations.sonarqubeTitle': { en: 'SonarQube', fun: 'SonarQube' },
  'help.integrations.kafkaTitle': { en: 'Kafka', fun: 'Kafka' },
  'help.integrations.usvcMigrationsTitle': {
    en: 'Migrations (usvc-migrations)',
    fun: 'Migrations (usvc-migrations)',
  },

  // --- AdminDashboardDocView ---
  'help.admin.whatTitle': {
    en: 'What the admin dashboard is',
    fun: 'What the Admin Dashboard Actually Does',
  },
  'help.admin.metricsTitle': {
    en: 'The seven metrics',
    fun: 'Magnificent Seven Metrics',
  },
  'help.admin.filtersTitle': {
    en: 'Filters: owner, environment, and time range',
    fun: 'Filters: Owner, Environment, and Time Range',
  },
  'help.admin.applicabilityTitle': {
    en: 'Filter applicability per metric',
    fun: 'Which Filter Works Where',
  },
  'help.admin.architectureTitle': {
    en: 'Backend architecture',
    fun: 'What Powers It Behind the Scenes',
  },
  'help.admin.platformHealthTitle': {
    en: 'Platform Health',
    fun: 'Vitals',
  },
  'help.admin.doraTitle': {
    en: 'DORA delivery metrics',
    fun: 'DORA Delivery Metrics',
  },
  'help.admin.accessTitle': { en: 'Access control', fun: 'Who Gets In' },
  'help.admin.table.metric': { en: 'Metric', fun: 'Metric' },
  'help.admin.table.source': { en: 'Source', fun: 'Source' },
  'help.admin.table.envFilter': {
    en: 'Environment filter',
    fun: 'Environment filter',
  },
  'help.admin.table.timeFilter': {
    en: 'Time-range filter',
    fun: 'Time-range filter',
  },

  // --- DeploymentsDocView ---
  'help.deployments.whatTitle': {
    en: 'What Deploy Management is',
    fun: 'What the Launch Pad Actually Does',
  },
  'help.deployments.matrixTitle': {
    en: 'The Service Deploy Matrix',
    fun: 'The Big Board',
  },
  'help.deployments.driftTitle': {
    en: 'How drift is computed',
    fun: 'How We Catch You Slacking',
  },
  'help.deployments.promoteTitle': {
    en: 'Promote & bulk promote',
    fun: 'Send It',
  },
  'help.deployments.ticketLookupTitle': {
    en: 'Find ticket',
    fun: 'Track Cargo',
  },
  'help.deployments.detailTitle': {
    en: 'Comparing two environments',
    fun: 'Spot the Difference',
  },
  'help.deployments.architectureTitle': {
    en: 'Backend architecture',
    fun: 'What Powers It Behind the Scenes',
  },

  // --- WorkflowsDocView ---
  'help.workflows.allTitle': {
    en: 'All workflows',
    fun: 'Every Workflow, All at Once',
  },
  'help.workflows.directBadge': { en: 'Direct', fun: 'No Gatekeeping' },

  // --- Shared section headings (WorkflowDetailView + ApprovalKindDetailView) ---
  'help.section.howItWorks': {
    en: 'How it works',
    fun: 'How It Actually Works',
  },
  'help.section.steps': { en: 'Steps', fun: 'Steps' },
  'help.section.explore': { en: 'Explore', fun: 'Go Explore' },

  // --- WorkflowDetailView / WorkflowDetailPage ---
  'help.workflowDetail.approvalTitle': { en: 'Approval', fun: 'Approval Part' },
  'help.workflowDetail.noApprovalAlert': {
    en: 'Runs directly — no approval required.',
    fun: 'Runs directly — no permission slip needed.',
  },
  'help.workflowDetail.runButton': {
    en: '▶ Run this workflow',
    fun: '▶ Fire This Thing Up',
  },
  'help.workflowDetail.notFoundTitle': {
    en: 'Workflow not found',
    fun: 'Workflow Not Found',
  },
  'help.workflowDetail.backToWorkflows': {
    en: 'Back to Workflows',
    fun: 'Back to the Button Factory',
  },

  // --- ApprovalKindDetailView / ApprovalKindDetailPage ---
  'help.approvalKindDetail.definitionTitle': {
    en: 'Kind definition',
    fun: 'Fine Print',
  },
  'help.approvalKindDetail.governsLabel': { en: 'Governs', fun: 'Governs' },
  'help.approvalKindDetail.approverGroupsLabel': {
    en: 'Approver groups',
    fun: 'Who Signs Off',
  },
  'help.approvalKindDetail.onApproveLabel': {
    en: 'On approve',
    fun: 'Once You Say Yes',
  },
  'help.approvalKindDetail.notFoundTitle': {
    en: 'Approval kind not found',
    fun: 'Approval Kind Not Found',
  },
  'help.approvalKindDetail.backToApprovals': {
    en: 'Back to Approvals',
    fun: 'Back to the Rulebook',
  },

  // --- RelationshipGraphView ---
  'help.relationshipGraph.cardTitle': {
    en: 'What each workflow does on approve',
    fun: 'What Happens When You Hit Approve',
  },
  'help.relationshipGraph.table.workflow': { en: 'Workflow', fun: 'Workflow' },
  'help.relationshipGraph.table.approvalKind': {
    en: 'Approval kind',
    fun: 'Approval kind',
  },
  'help.relationshipGraph.table.effect': { en: 'Effect', fun: 'Effect' },
  'help.relationshipGraph.table.targetRepo': {
    en: 'Target repo',
    fun: 'Target repo',
  },
  'help.relationshipGraph.noneDirect': {
    en: 'none — direct',
    fun: 'none — just does it',
  },

  // --- RelatedLinks ---
  'help.related.label': { en: 'Related', fun: 'Also Worth a Look' },
  'help.related.homePanels': { en: 'Home panels', fun: 'Peek at Home panels' },
  'help.related.integrationsBehindPanels': {
    en: 'Integrations behind these panels',
    fun: 'What powers these panels',
  },
  'help.related.catalogOwnership': {
    en: 'Catalog ownership',
    fun: 'Who owns what in the Catalog',
  },
  'help.related.gitopsUpdateWorkflows': {
    en: 'Gitops update workflows',
    fun: 'GitOps update workflows',
  },
  'help.related.gitopsApprovalKinds': {
    en: 'Gitops approval kinds',
    fun: 'GitOps approval squad',
  },
  'help.related.updatingGitopsConfig': {
    en: 'Updating gitops config',
    fun: 'How to update GitOps config',
  },
  'help.related.changingServiceConfig': {
    en: 'Changing service config',
    fun: 'How to change service config',
  },
  'help.related.promoteReleaseWorkflow': {
    en: 'Promote & release workflow',
    fun: 'The promote & release workflow',
  },
  'help.related.rolloutRestartWorkflow': {
    en: 'Roll out (restart) workflow',
    fun: 'The roll-out (restart) workflow',
  },
  'help.related.releaseApprovalKinds': {
    en: 'Release approval kinds',
    fun: 'The release approval squad',
  },
  'help.related.personalHomeDashboard': {
    en: 'Personal Home dashboard',
    fun: 'Your own Home dashboard',
  },
  'help.related.integrationsBehindMetrics': {
    en: 'Integrations behind these metrics',
    fun: 'What powers these metrics',
  },
  'help.related.githubIntegration': {
    en: 'GitHub integration',
    fun: 'GitHub integration',
  },
  'help.related.sentryIntegration': {
    en: 'Sentry integration',
    fun: 'Sentry integration',
  },
  'help.related.sonarqubeIntegration': {
    en: 'SonarQube integration',
    fun: 'SonarQube integration',
  },
  'help.related.kafkaIntegration': {
    en: 'Kafka integration',
    fun: 'Kafka integration',
  },
  'help.related.catalogEntities': {
    en: 'Catalog entities',
    fun: 'Catalog entities',
  },
  'help.related.catalogGroups': { en: 'Catalog groups', fun: 'Catalog groups' },
  'help.related.usvcMigrationsIntegration': {
    en: 'Migrations integration',
    fun: 'Migrations integration',
  },
  'help.related.catalogTabs': { en: 'Catalog entity tabs', fun: 'Catalog entity tabs' },
  'help.related.createGithubRepoWorkflow': {
    en: 'Create GitHub repository workflow',
    fun: 'Create GitHub repository workflow',
  },
  'help.related.runUsvcMigrationWorkflow': {
    en: 'Run usvc-migration workflow',
    fun: 'Run usvc-migration workflow',
  },
};
